import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import get from 'lodash.get';

import { liveBlogCard } from 'lib/CustomPropTypes';

import { getBodyItemComponent } from 'components/Article/getBodyItemComponent';
import InlineVideo from 'components/InlineVideo';
import SocialShareList from 'components/Social/ShareList';

import Thumbnail from 'components/Thumbnail';
import TimeAgo from 'components/TimeAgo';

import CardHeadline from 'components/Card/CardHeadline';
import AIMS_FLAVORS from 'lib/aimsFlavors';
import { getSiteConfigs } from 'lib/vertical';
import { Byline } from 'components/Byline';
import { isValidUrl } from 'lib/urlUtils';
import Link from 'components/Link';

const cardMarkup = (
  markupAndEmbeds,
  amp,
  path,
) => markupAndEmbeds.map((markup, index) => getBodyItemComponent(markup, index, amp, path));

const byThumbnail = (thumbnail, label, to) => (
  <div className={classNames('live-blog-card__byline-thumbnail', 'mt1 mr2')} data-test="thumbnail">
    <ConditionalLink to={to}>
      <Thumbnail
        thumbnail={thumbnail}
        responsiveFlavors={{ s: AIMS_FLAVORS.FOCAL_100x100 }}
        label={label}
      />
    </ConditionalLink>
  </div>
);

const bylineContainer = (authors, date) => {
  const { primaryImage } = (authors && authors[0]) || {};
  const oneAuthorOnly = authors && authors.length === 1;

  return (
    <div className={classNames('live-blog-card__byline', 'mb6 df')} data-test="byline-container">
      {
        oneAuthorOnly
        && primaryImage
        && primaryImage.url
        && byThumbnail(
          primaryImage.url.primary,
          primaryImage.altText,
          get(authors, [0, 'url', 'primary'], ''),
        )
      }
      <div className="df flex-column">
        {
          authors
          && <p className="f4 founders-cond mv0 gray-80" data-test="byline"><Byline authors={authors} /></p>
        }
        {date && <TimeAgo dateString={date.modifiedAt} />}
      </div>
    </div>
  );
};

const ConditionalLink = ({ to, children }) => {
  if (typeof to !== 'undefined' && isValidUrl(to)) {
    return (
      <Link to={to}>
        {children}
      </Link>
    );
  }
  return children;
};

const Card = ({
  cardType,
  authors,
  content: { markupAndEmbeds },
  date,
  headline: { primary: primaryHed },
  isHeadlineHidden = false,
  id,
  path,
  canonicalUrl,
  url,
  cardData: videoData,
}, { amp, vertical }) => {
  const cardClasses = classNames(
    'live-blog-card',
    'article-body__content',
    'w-100 mh0 mb8 fn overflow-x-hidden',
  );

  let socialUrl = url.primary;
  if (canonicalUrl) {
    const [canonical, qs] = canonicalUrl.split('?');
    socialUrl = `${canonical}/${id}${qs ? `?${qs}` : ''}`;
  }

  const text = encodeURIComponent(primaryHed);

  const { twitterIntentVia: via } = getSiteConfigs(vertical);

  const socialProps = {
    twitterHref: `https://twitter.com/intent/tweet?text=${text}&via=${via}&url=${socialUrl}&original_referer=${socialUrl}`,
    facebookHref: `https://www.facebook.com/sharer/sharer.php?u=${socialUrl}`,
    emailHref: `mailto:?subject=${primaryHed}&body=${socialUrl}`,
    classes: 'df justify-start mt6 lh-none',
    tracking: 'mbt_navbar_socialShare',
  };

  return (
    <div
      className={cardClasses}
      key={id}
      // amp attributes
      id={id}
      data-sort-time={date.publishedAt}
      data-update-time={date.modifiedAt}
      data-test="card-container"
    >
      <div className={classNames('live-blog__card__stripes')} data-test="stripes" />
      {bylineContainer(authors, date)}
      { !isHeadlineHidden && (
        <CardHeadline
          primaryHed={primaryHed}
          path={path}
          id={id}
        />
      )}
      {cardMarkup(markupAndEmbeds, amp, path)}
      {cardType === 'VIDEO' && <InlineVideo video={videoData} />}
      <SocialShareList {...socialProps} />
    </div>
  );
};


Card.propTypes = liveBlogCard;

Card.contextTypes = {
  amp: PropTypes.bool,
  vertical: PropTypes.string,
};

export default Card;
